import socket from "../config/socket";
import { setRealtimeEvent } from "./realtimeSlice";
import { getRealtimeHandlers } from "./realtimeModuleHandlers";

let initialized = false;
let unsubscribeHandler = null;
let queue = [];
let flushTimer = null;
const handledKeys = new Map();
const lastRefetchAt = new Map();

const DEFAULT_DEBOUNCE_MS = 120;
const DEDUPE_TTL_MS = 5000;
const REFRESH_COOLDOWN_MS = 800;
const ALLOWED_ACTIONS = new Set(["created", "updated", "deleted"]);
const MODULE_ALIASES = {
  managerprojectrequested: "project",
  "project-assigned": "tasks",
  reassigned: "tasks",
  "submit-task": "tasks",
  projectaction: "tasks",
  employees: "users",
  user: "users",
  clientrating: "dashboard",
};

const getDedupKey = (event, payload) => {
  const data = payload?.data;
  const id = data?._id || data?.id || data?.quotationId || data?.projectid || "";
  const updatedAt = data?.updatedAt || data?.createdAt || "";
  return `${event}|${id}|${updatedAt}`;
};

const cleanupHandledKeys = (now) => {
  handledKeys.forEach((time, key) => {
    if (now - time > DEDUPE_TTL_MS) handledKeys.delete(key);
  });
};

const parseEventName = (event, payload = {}) => {
  const full = `${event || ""}`;
  const colonIdx = full.indexOf(":");
  const moduleName =
    colonIdx === -1 ? full.trim() : full.slice(0, colonIdx).trim();
  const actionName =
    colonIdx === -1 ? "" : full.slice(colonIdx + 1).trim();
  const rawModule = (moduleName || payload.module || "").toLowerCase().trim();
  const normalizedModule = MODULE_ALIASES[rawModule] || rawModule;
  const normalizedAction = (actionName || payload.action || "").toLowerCase().trim();

  return {
    module: normalizedModule,
    action: normalizedAction,
    rawModule,
  };
};

// const parseEventName = (event, payload = {}) => {
//   const [moduleName, actionName] = `${event || ""}`.split(":");
//   const rawModule = (moduleName || payload.module || "").toLowerCase().trim();
//   const normalizedModule = MODULE_ALIASES[rawModule] || rawModule;
//   const normalizedAction = (actionName || payload.action || "").toLowerCase().trim();

//   return {
//     module: normalizedModule,
//     action: normalizedAction,
//     rawModule,
//   };
// };

const flushQueue = (dispatch) => {
  if (!queue.length) return;
  const items = queue;
  queue = [];

  for (const { event, payload, module, action, rawModule } of items) {
    dispatch(
      setRealtimeEvent({
        event,
        module,
        originalModule: rawModule,
        action,
        data: payload?.data ?? payload ?? null,
        receivedAt: Date.now(),
      })
    );

    // Optional central refresh strategy:
    // module handlers can trigger re-fetch without touching component logic.
    const handlers = getRealtimeHandlers(module);
    if (!handlers.length) continue;

    const now = Date.now();
    handlers.forEach((createThunkAction, index) => {
      const thunkKey = `${module}:${action}:handler-${index}`;
      const prev = lastRefetchAt.get(thunkKey) || 0;
      if (now - prev < REFRESH_COOLDOWN_MS) return;
      lastRefetchAt.set(thunkKey, now);
      const thunkAction = createThunkAction();
      if (thunkAction) {
        dispatch(thunkAction);
      }
    });
  }
};

export const initRealtimeListeners = (dispatch, options = {}) => {
  if (initialized || typeof dispatch !== "function") return;
  initialized = true;

  const debounceMs = Number(options.debounceMs) || DEFAULT_DEBOUNCE_MS;

  const onAnyEvent = (event, payload = {}) => {
    if (typeof event !== "string" || !event.includes(":")) return;

    const now = Date.now();
    cleanupHandledKeys(now);
    const dedupKey = getDedupKey(event, payload);
    if (handledKeys.has(dedupKey)) return;
    handledKeys.set(dedupKey, now);

    const { module, action, rawModule } = parseEventName(event, payload);
    if (!module || !action) return;
    if (!ALLOWED_ACTIONS.has(action)) return;

    queue.push({ event, payload, module, action, rawModule });

    if (flushTimer) clearTimeout(flushTimer);
    flushTimer = setTimeout(() => {
      flushTimer = null;
      flushQueue(dispatch);
    }, debounceMs);
  };

  socket.onAny(onAnyEvent);

  unsubscribeHandler = () => {
    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
    queue = [];
    handledKeys.clear();
    lastRefetchAt.clear();
    socket.offAny(onAnyEvent);
    initialized = false;
    unsubscribeHandler = null;
  };
};

export const removeRealtimeListeners = () => {
  if (typeof unsubscribeHandler === "function") {
    unsubscribeHandler();
  }
};
